import { Dispatch, SetStateAction, use, useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';

import Device from 'features/device/domain/models/device';
import { useReadDevice } from 'features/device/domain/queries/device-queries';

export type DeviceDetailsState = {
  currentDevice?: Device;
  currentViewingMode: ViewingMode;
  isLoadingDevice: boolean;
  isSavingDevice: boolean;
  form: UseFormReturn<Partial<Device>, any>;
  linkedOrganisationErrorMessage?: string;
  resetForm: (device?: Device) => void;
  setIsLoadingDevice: (isLoading: boolean) => void;
  setIsSavingDevice: (isSaving: boolean) => void;
  setCurrentViewingMode: (viewingMode: ViewingMode) => void;
  reloadCurrentDeviceById: (deviceId: string | undefined) => void;
  setLinkedOrganisationErrorMessage: Dispatch<SetStateAction<string | undefined>>;
  unlinkWarningConfirmed: boolean;
  updateUnlinkWarningConfirmed: (confirmed: boolean) => void;
};

type Props = {
  deviceId: string | undefined;
  initialViewingMode: ViewingMode;
};

export const useDeviceDetailsState = ({ deviceId, initialViewingMode }: Props): DeviceDetailsState => {
  const { enablePopUpGuard } = use(ConfirmationPopupProviderContext);

  const [currentDevice, setCurrentDevice] = useState<Device | undefined>();
  const [currentViewingMode, setCurrentViewingMode] = useState<ViewingMode>(initialViewingMode);
  const [currentDeviceId, setCurrentDeviceId] = useState<string | undefined>(deviceId);
  const [isLoadingDevice, setIsLoadingDevice] = useState<boolean>(false);
  const [isSavingDevice, setIsSavingDevice] = useState<boolean>(false);
  const [linkedOrganisationErrorMessage, setLinkedOrganisationErrorMessage] = useState<string>();
  const [unlinkWarningConfirmed, setUnlinkWarningConfirmed] = useState<boolean>(false);

  const {
    data,
    isFetching: isLoadingReadDevice,
    refetch: triggerReadDevice,
  } = useReadDevice(currentDeviceId ?? '', currentDeviceId !== undefined);

  const reloadCurrentDeviceById = (deviceId: string | undefined): void => {
    if (deviceId === undefined) {
      return;
    }

    if (deviceId === currentDeviceId) {
      triggerReadDevice();
    } else {
      setCurrentDeviceId(deviceId);
    }
  };

  const defaultValues: Partial<Device> = {
    name: '',
    note: '',
    type: undefined,
    function: undefined,
    source: '',
    address: '',
    organisationUnitId: undefined,
    gatewayId: undefined,
    systemId: undefined,
    externalId: '',
    commands: [],
    deviceMonitoring: undefined,
    detectionLoopNumber: undefined,
    eventTypeLow: undefined,
    eventTypeHigh: undefined,
    intercomMappings: [],
  };

  const form = useForm({
    defaultValues,
    mode: 'onBlur',
  });

  const resetForm = (device?: Device): void => {
    if (!device) {
      form.reset(defaultValues);
      return;
    }

    form.reset({
      ...device,
      eventTypeLow: device.domipEvent?.eventTypeLow,
      eventTypeHigh: device.domipEvent?.eventTypeHigh,
    });
  };

  const updateUnlinkWarningConfirmed = (confirmed: boolean) => {
    setUnlinkWarningConfirmed(confirmed);
  };

  useEffect(() => {
    if (deviceId !== currentDeviceId) {
      setCurrentDeviceId(deviceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  useEffect(() => {
    if (currentDeviceId !== undefined) {
      triggerReadDevice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDeviceId]);

  useEffect(() => {
    if (data && !isLoadingReadDevice) {
      setCurrentDevice(data);
      resetForm(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoadingReadDevice]);

  useEffect(() => {
    setIsLoadingDevice(isLoadingReadDevice);
  }, [isLoadingReadDevice]);

  useEffect(() => {
    enablePopUpGuard(form.formState.isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.formState.isDirty]);

  useEffect(() => {
    setLinkedOrganisationErrorMessage(undefined);
  }, [currentViewingMode]);

  return {
    currentDevice: currentDevice,
    currentViewingMode: currentViewingMode,
    isLoadingDevice,
    isSavingDevice,
    form,
    linkedOrganisationErrorMessage,
    resetForm,
    setIsLoadingDevice,
    setIsSavingDevice,
    setCurrentViewingMode,
    reloadCurrentDeviceById,
    setLinkedOrganisationErrorMessage,
    unlinkWarningConfirmed,
    updateUnlinkWarningConfirmed,
  };
};
